import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, X as XClose } from "lucide-react";
import { CowMark } from "./CowMark";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AiCowChat = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "cow", key: "chat.greeting" }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    let id = localStorage.getItem("solmoo-chat-session");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("solmoo-chat-session", id);
    }
    sessionRef.current = id;
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "cow", text: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId: sessionRef.current, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const chunk = await reader.read();
        if (chunk.done) break;
        buf += decoder.decode(chunk.value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const line of lines) {
          const payload = line.replace(/^data:\s*/, "");
          if (!payload) continue;
          if (payload === "[DONE]") continue;
          try {
            const j = JSON.parse(payload);
            if (j.delta) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "cow", text: c[c.length - 1].text + j.delta };
                return c;
              });
            }
            if (j.error) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "cow", text: c[c.length - 1].text || j.error };
                return c;
              });
            }
          } catch (e) {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "cow", text: t("chat.error") };
        return c;
      });
    }
    setBusy(false);
  };

  return (
    <div className="fixed bottom-5 right-5 z-[80] flex flex-col items-end gap-3 sm:bottom-6 sm:right-6">
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="flex h-[520px] max-h-[72vh] w-[370px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-3xl border border-purple-500/30 bg-[#0E0E16] shadow-[0_24px_70px_rgba(0,0,0,0.6)]"
          >
            <div className="flex items-center justify-between border-b border-purple-500/15 px-5 py-4">
              <div className="flex items-center gap-3">
                <CowMark size={36} />
                <div>
                  <div className="font-display text-sm font-bold text-[#F5F3FF]">
                    {t("chat.title")}
                  </div>
                  <div className="font-display text-[11px] font-bold uppercase tracking-[0.15em] text-purple-300">
                    Powered by Gemini
                  </div>
                </div>
              </div>
              <button
                data-testid="ai-chat-close"
                onClick={() => setOpen(false)}
                className="rounded-lg p-1.5 text-gray-400 transition-colors hover:text-[#F5F3FF]"
                aria-label="Close chat"
              >
                <XClose size={18} />
              </button>
            </div>

            <div
              ref={scrollRef}
              className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
              data-testid="ai-chat-messages"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`ai-chat-message-${i}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-solana-gradient ml-auto text-white"
                      : "border border-purple-500/20 bg-[#14141E] text-gray-200"
                  }`}
                >
                  {m.key ? t(m.key) : m.text || (busy && i === messages.length - 1 ? "…" : m.text)}
                </div>
              ))}
              {busy && messages[messages.length - 1].text === "" && (
                <div className="font-display text-xs font-bold text-gray-500">
                  {t("chat.thinking")}
                </div>
              )}
              {messages.length === 1 && !busy && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {["chat.s1", "chat.s2", "chat.s3"].map((s) => (
                    <button
                      key={s}
                      data-testid="ai-chat-suggestion"
                      onClick={() => send(t(s))}
                      className="rounded-full border border-purple-500/30 bg-[#14141E] px-3 py-1.5 text-xs text-purple-200 transition-colors hover:border-[#14F195]/60 hover:text-[#14F195]"
                    >
                      {t(s)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 border-t border-purple-500/15 p-3">
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={t("chat.placeholder")}
                className="flex-1 rounded-xl border border-purple-500/20 bg-[#14141E] px-4 py-2.5 text-sm text-[#F5F3FF] placeholder-gray-600 outline-none focus:border-[#14F195]/50"
              />
              <button
                data-testid="ai-chat-send"
                onClick={() => send()}
                disabled={busy || !input.trim()}
                className="bg-solana-gradient flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white transition-transform hover:scale-105 disabled:opacity-40"
                aria-label="Send message"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen((v) => !v)}
        className="group flex items-center gap-2.5 rounded-full border border-purple-500/30 bg-[#0E0E16] py-1.5 pl-1.5 pr-4 shadow-[0_12px_40px_rgba(153,69,255,0.3)] transition-transform hover:scale-105"
        aria-label="Ask the Cow AI chat"
      >
        <CowMark size={40} />
        <span className="font-display pr-1 text-sm font-bold text-[#F5F3FF]">
          {t("chat.ask")}
        </span>
        <span className="h-2 w-2 rounded-full bg-[#14F195] animate-pulse-dot" />
      </button>
    </div>
  );
};
